import Translator from "@/utils/common/Translator.ts";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import MetaData from "@/utils/common/MetaData.ts";
import {ColDef, ColGroupDef, ColumnState, SuppressHeaderKeyboardEventParams, SuppressKeyboardEventParams} from "ag-grid-community";
import AGGridEditViewColumnFactory from "@/utils/UIConstants/AGGridEditViewColumns/AGGridEditViewColumnFactory.ts";
import ListViewUtils from "@/utils/common/ListViewUtils.ts";
import AGGridActionsColumnRenderer from "@/components/inputs/AGGridActionsColumnRenderer.vue";
import EditableGridHandler from "@/utils/presenters/EditableGridHandler.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import AttachmentLinkUtil from "@/utils/UIConstants/AttachmentLinkUtil.ts";
import DownloadPurpose from "@/utils/constants/DownloadPurpose.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import {GridApi} from "ag-grid-enterprise";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import AbsAGGridEditViewColumn from "@/utils/UIConstants/AGGridEditViewColumns/AbsAGGridEditViewColumn.ts";
import EditScreenGridColumn = namaTypes.EditScreenGridColumn;
import EditScreenGrid = namaTypes.EditScreenGrid;
import EntityReferenceData = namaTypes.EntityReferenceData;
import FieldMetaData = namaTypes.FieldMetaData;

export default class EditScreenGridHandler extends EditableGridHandler<EditPresenter>
{
  private readonly _grid: EditScreenGrid;
  private _api: GridApi | undefined;
  private _defaultHiddenColIds: string[] = [];

  constructor(grid: namaTypes.EditScreenGrid, presenter: EditPresenter) {
    super(grid.id!, presenter);
    this._grid = grid;
  }

  get grid(): namaTypes.EditScreenGrid {
    return this._grid;
  }

  get api(): GridApi | undefined {
    return this._api;
  }

  addGridApi(api: GridApi | undefined) {
    this._api = api;
  }

 override listVisibleColumns() {
    const visibleColumns: EditScreenGridColumn[] = [];
    for (let column of this._grid.columns ?? []) {
      if (column.showInGridView && (!column.showOnlyInLang || column.showOnlyInLang === Translator.currentLang()))
        visibleColumns.push(column);
    }
    return visibleColumns;
  }

  override listCurrentlyVisibleColumns(): EditScreenGridColumn[] {
    if (!this._api)
      return this.listVisibleColumns();
    let columnState: ColumnState[] | undefined;
    try {
      columnState = this._api.getColumnState();
    } catch {
      return this.listVisibleColumns();
    }
    if (!columnState || !columnState.length)
      return this.listVisibleColumns();
    const visibleColIds = new Set(columnState.filter(state => !state.hide).map(state => state.colId));
    const visibleColumns: EditScreenGridColumn[] = [];
    for (let column of this._grid.columns ?? []) {
      if (visibleColIds.has(AGGridUtils.encodeColId(column.id!)))
        visibleColumns.push(column);
    }
    return visibleColumns;
  }

  fetchColDefs(): (ColDef | ColGroupDef)[] {
    let visibleColumns = new Set(this.listVisibleColumns());
    let columnDefs: (ColDef | ColGroupDef)[] = [];
    this._defaultHiddenColIds = [];
    for (let column of this._grid.columns ?? []) {
      let hidden = false;
      if (!visibleColumns.has(column) /*TODO || NestMode.actualNestActive()*/)
        hidden = true;
      if (!SecurityUtils.userCanView(this.entityType, column.id!, this.presenter.calcEditModeApplicability()))
        continue;
      if (this.isCustomColumn(column)) {
        /*TODO :: Create Custom Column*/
        continue;
      }
      let fieldMetadata = MetaData.findFieldMetadata(column.id!, this.metadata);
      if (fieldMetadata && !SecurityUtils.userCanFeature(fieldMetadata.relatedToFeature))
        continue;
        this.presenter.visibleGridColumns.push(column);
      if (!fieldMetadata) {
        /*TODO*/
        continue;
      }
      let colDef: AbsAGGridEditViewColumn | null = this.createActualColumn(column);
      if (colDef) {
        const builtColDefs = this.buildColumnDefs(column, fieldMetadata, colDef);
        columnDefs.push(...builtColDefs);
        if (hidden)
          this.collectDefaultHiddenColIds(builtColDefs);
      }
    }
    ListViewUtils.configureColumnGrouping(columnDefs);
    if (this._grid.actions?.length)
      columnDefs.push(this.createActionsColumn())
    return columnDefs;
  }

  fetchDefaultHiddenColIds(): string[] {
    return this._defaultHiddenColIds;
  }

  private collectDefaultHiddenColIds(colDefs: (ColDef | ColGroupDef)[]) {
    for (const colDef of colDefs) {
      if ('colId' in colDef && colDef.colId)
        this._defaultHiddenColIds.push(colDef.colId);
    }
  }

  private buildColumnDefs(column: EditScreenGridColumn, fieldMetadata: FieldMetaData, colDef: AbsAGGridEditViewColumn): (ColDef | ColGroupDef)[] {
    const createdColDefs = colDef.createColDef();
    const isReference = fieldMetadata.fieldType === FieldType.Reference || fieldMetadata.fieldType === FieldType.Genericreference;
    if (!isReference)
      return createdColDefs;
    const imageColDef = this.createSeparateImageColumn(column, fieldMetadata, colDef);
    if (!imageColDef)
      return createdColDefs;
    const refHeaderName = Translator.translateTitledId(this.entityType, column);
    const groupPrefix = refHeaderName.includes('|') ? refHeaderName.substring(0, refHeaderName.indexOf('|')) : refHeaderName.trim();
    for (const cd of createdColDefs) {
      if (!(cd as any).children)
        (cd as any).headerName = groupPrefix + '|' + '';
    }
    imageColDef.headerName = groupPrefix + '|' + '';
    return [...createdColDefs, imageColDef];
  }

  createActionsColumn(): ColDef {
    return {
      editable: false,
      colId: IdsAndConstants.ACTIONS_COLUMN,
      cellRenderer: AGGridActionsColumnRenderer,
      cellRendererParams: {
        actions: this._grid.actions,
        presenter: () => this.presenter,
      },
      cellClass: "tw-flex tw-items-center nama-full-width-cell",
    };
  }

  fetchDefaultColDef() {
    return {
      editable: true,
      enableCellChangeFlash: true,
      autoHeight: true,
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams): boolean => {
        return params.event.ctrlKey && params.event.key === "Delete";
      }, suppressHeaderKeyboardEvent: (params: SuppressHeaderKeyboardEventParams): boolean => {
        return params.event.ctrlKey && params.event.key === "Delete";
      }
    } as ColDef;
  }

  private createSeparateImageColumn(column: EditScreenGridColumn, fieldMetadata: namaTypes.FieldMetaData, refColDef: any): ColDef | null {
    const referencedEntityType = fieldMetadata.referencedEntityType;
    if (!referencedEntityType)
      return null;
    const imageInfo = ClientCache.getEntityImageInfo(referencedEntityType);
    if (!imageInfo || !imageInfo.showInSeparateColumn)
      return null;
    const trimmedFieldId = refColDef.trimmedFieldId;
    const width = imageInfo.widthOfSeparateColumns || 50;
    return {
      colId: AGGridUtils.encodeColId(column.id + '.image'),
      field: AGGridUtils.encodeColId(trimmedFieldId),
      headerName: '',
      editable: false,
      sortable: false,
      filter: false,
      width: width,
      maxWidth: width + 20,
      suppressHeaderMenuButton: true,
      valueGetter: (params) => {
        return NamaCommon.getFieldValue(params.data, trimmedFieldId);
      },
      cellRenderer: (params: any) => {
        const ref: EntityReferenceData = params.value;
        if (!ref || !ref.id || !ref.hasImage)
          return '';
        const url = AttachmentLinkUtil.downloadURLByRecordId(ref, DownloadPurpose.THUMBNAIL, false);
        return `<img src="${url}" style="max-height:${imageInfo.heightOfSeparateColumns || 40}px;max-width:${width}px;object-fit:contain;padding:6px;" />`;
      }
    };
  }

  private isCustomColumn(column: namaTypes.EditScreenGridColumn) {
    let fieldMetadata = MetaData.findFieldMetadata(column.id!, this.metadata);
    // TODO::
    // if (column.id === IdsAndConstants.details_item_item && fieldMetadata && fieldMetadata.referencedEntityType === SCEntities.InvItem)
    //   return true;
    // if (this.metadata.owner && column.id === IdsAndConstants.lines_account && ObjectChecker.isAnyEqualToFirst(this.metadata.owner.ownerId, ACCEntities.JournalEntry,
    //   ACCEntities.BankAdjustment, ACCEntities.InterCompanyTransfer, ACCEntities.CurrencyDiffJournal, ACCEntities.ReceiptVoucher,
    //   ACCEntities.PaymentVoucher))
    //   return true
    return false;
  }

  private createActualColumn(column: EditScreenGridColumn) {
    let columnMetadata = MetaData.findFieldMetadata(column.id!, this.metadata);
    if (!columnMetadata.fieldType) {
      console.log("**IGNORED_COLUMN****: Could not find info for column " + column.id)
      return null;
    }
    return AGGridEditViewColumnFactory.createColDefForType(column, this);
  }
}
